import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [connectionError, setConnectionError] = useState(false);

  // Set up axios interceptor for authentication
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token expiration
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        } else if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
          setConnectionError(true);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token]);

  // Check if user is logged in on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (token) {
        try {
          setConnectionError(false);
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            timeout: 10000
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
            setConnectionError(true);
          } else {
            localStorage.removeItem('authToken');
            setToken(null);
          }
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, [token]);

  const login = async (email, password, organizationSlug = null, isSuperAdmin = false) => {
    try {
      setConnectionError(false);
      
      // Determine login endpoint and payload based on context
      let endpoint, payload;
      
      if (isSuperAdmin) {
        // Super Admin login - direct authentication
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`;
        payload = { email, password };
      } else {
        // Organization login - use new fixed endpoint
        if (!organizationSlug) {
          throw new Error('Organization slug (account name) is required for organization login');
        }
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/org-login`;
        payload = { organization_slug: organizationSlug, email, password };
      }
      
      const response = await axios.post(endpoint, payload, {
        timeout: 10000
      });
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('authToken', access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        setConnectionError(true);
        return { 
          success: false, 
          error: 'Cannot connect to server. Please check your internet connection or try again later.' 
        };
      }
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      setConnectionError(false);
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, userData, {
        timeout: 10000
      });
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        setConnectionError(true);
        return { 
          success: false, 
          error: 'Cannot connect to server. Please check your internet connection or try again later.' 
        };
      }
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setConnectionError(false);
    // Redirect to login page after logout using window.location
    window.location.href = '/login';
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    connectionError,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isSuperAdmin: user?.role === 'super_admin',
    isSupervisor: user?.role === 'supervisor'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};